import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { FaEnvelope, FaArrowLeft, FaCheckCircle, FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { authApi } from '../../api/auth';
import { STORAGE_KEYS } from '../../api/config';
import './ForgotPassword.css';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: request, 2: verify, 3: reset
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [authorizationCode, setAuthorizationCode] = useState('');

  // Form data
  const [formData, setFormData] = useState({
    email: '', // email or phone number
    token: '',
    new_password: '',
    new_password_confirmation: ''
  });

  const [resetEmail, setResetEmail] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  // Step 1: Request password reset
  const handleRequestReset = async (e) => {
    e.preventDefault();

    if (!formData.email.trim()) {
      setError('Tafadhali weka namba yako ya simu au barua pepe');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await authApi.requestOtp({
        number: formData.email,
        password_reset_request: true
      });

      if (response.success || response.status === 800 || response.status === 200) {
        setSuccess('Namba ya uthibitisho (OTP) imetumwa kikamilifu. Tafadhali angalia simu au barua pepe yako.');
        setStep(2);
        toast.success('OTP imetumwa!');
      } else {
        setError(response.message || 'Hitilafu katika kutuma OTP');
      }
    } catch (err) {
      console.error('Reset request error:', err);
      // Surface server-side error details for easier debugging
      const serverMessage =
        err?.message ||
        err?.error ||
        err?.data?.message ||
        null;
      if (serverMessage) {
        setError(serverMessage);
      } else {
        setError('Server error (500). The server failed to process the request. Please check the backend logs or try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify Token
  const handleVerifyToken = async (e) => {
    e.preventDefault();

    if (!formData.token.trim()) {
      setError('Tafadhali weka OTP');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await authApi.verifyOtp({
        otp: formData.token,
        number: formData.email
      });

      if (response.success || response.status === 800 || response.status === 200) {
        const tokenStr = response.data?.authorization_code || response.data?.token || response.data || '';
        const code = response.data?.authorization_code || response.data?.token || response.data;
        const codeVal = typeof code === 'object' ? (code.authorization_code || code.token) : code;

        if (response.data?.token) {
          localStorage.setItem(STORAGE_KEYS.TOKEN, response.data.token);
        }
        if (response.data?.user_data) {
          const userDataToStore = { ...response.data.user_data };
          if (response.data.events) {
            userDataToStore.events = response.data.events;
          }
          localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userDataToStore));
          localStorage.setItem(STORAGE_KEYS.USER_ID, userDataToStore.id);
        }
        if (response.data?.events && response.data.events[0]) {
          localStorage.setItem(STORAGE_KEYS.UID, response.data.events[0].event_address);
          localStorage.setItem(STORAGE_KEYS.EVENT_ID, response.data.events[0].id);
          localStorage.setItem('selectedEventId', response.data.events[0].id?.toString());
        }

        // Store temp auth code and show change password modal flag
        localStorage.setItem('temp_auth_code', response.data?.authorization_code || codeVal || '');
        localStorage.setItem('show_change_password_modal', 'true');
        localStorage.setItem('reset_mobile', response.data?.user_data?.mobile || response.data?.user_data?.user_name || formData.email);

        setSuccess('Uthibitisho umekamilika! Inakupeleka kwenye dashibodi...');
        toast.success('Uthibitisho umekamilika!');
        
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 1500);
      } else {
        setError(response.message || 'OTP si sahihi au imeisha muda wake');
      }
    } catch (err) {
      console.error('Token verification error:', err);
      setError(err.message || 'OTP si sahihi au imeisha muda wake. Tafadhali omba OTP mpya.');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Reset password
  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (formData.new_password.length < 8) {
      setError('Password lazima iwe na herufi 6 au zaidi');
      return;
    }

    if (formData.new_password !== formData.new_password_confirmation) {
      setError('Password hazifanani');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await authApi.resetPassword({
        authorization_code: authorizationCode || localStorage.getItem(STORAGE_KEYS.TOKEN) || formData.token,
        new_password: formData.new_password,
        mobile: formData.email
      });

      if (response.success || response.status === 800 || response.status === 200) {
        setSuccess('Password imebadilishwa kikamilifu! Inakupeleka kwenye dashibodi...');
        toast.success('Password imebadilishwa kikamilifu!');

        setTimeout(() => {
          const userDataStr = localStorage.getItem(STORAGE_KEYS.USER);
          let userRole = 1;
          if (userDataStr) {
            try {
              const userData = JSON.parse(userDataStr);
              userRole = userData.role;
            } catch (e) { }
          }
          if (userRole === 3 || userRole === 'provider') {
            window.location.href = '/service-provider';
          } else {
            window.location.href = '/dashboard';
          }
        }, 2000);
      } else {
        setError(response.message || 'Hitilafu katika kubadilisha password');
      }
    } catch (err) {
      console.error('Password reset error:', err);
      setError(err.message || 'Hitilafu katika kubadilisha password. Tafadhali jaribu tena.');
    } finally {
      setLoading(false);
    }
  };

  // Resend reset link
  const handleResendLink = async () => {
    setLoading(true);
    try {
      const response = await authApi.requestOtp({
        number: formData.email,
        password_reset_request: true
      });

      if (response.success || response.status === 800 || response.status === 200) {
        toast.success('OTP mpya imetumwa kikamilifu!');
        setSuccess('OTP mpya imetumwa kwenye namba au barua pepe yako.');
      } else {
        toast.error('Hitilafu katika kutuma OTP mpya');
      }
    } catch (err) {
      toast.error('Hitilafu katika kutuma OTP mpya');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid className="forgot-password-container py-5">
      <Row className="justify-content-center align-items-center min-vh-100">
        <Col xs={12} md={8} lg={6} xl={5}>
          <Card className="forgot-password-card shadow-lg">
            <Card.Body className="p-4 p-md-5">
              {/* Header */}
              <div className="text-center mb-4">
                <div className="reset-icon-wrapper mb-3">
                  {step === 1 && <FaEnvelope size={48} className="text-primary" />}
                  {step === 2 && <FaCheckCircle size={48} className="text-success" />}
                  {step === 3 && <FaEnvelope size={48} className="text-primary" />}
                </div>
                <h3 className="fw-bold mb-2" style={{ color: '#1a237e' }}>
                  {step === 1 && 'Nimesahau Password'}
                  {step === 2 && 'Weka Namba ya Uthibitisho (OTP)'}
                  {step === 3 && 'Tengeneza Password Mpya'}
                </h3>
                <p className="text-muted">
                  {step === 1 && 'Weka namba yako ya simu au barua pepe ili kupokea namba ya uthibitisho (OTP)'}
                  {step === 2 && `Tumetuma OTP kwenda ${formData.email}`}
                  {step === 3 && 'Weka password yako mpya hapa chini'}
                </p>
              </div>

              {/* Alerts */}
              {error && (
                <Alert variant="danger" className="mb-4" onClose={() => setError('')} dismissible>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert variant="success" className="mb-4" onClose={() => setSuccess('')} dismissible>
                  {success}
                </Alert>
              )}

              {/* Step 1: Request Reset Form */}
              {step === 1 && (
                <Form onSubmit={handleRequestReset}>
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold">
                      <FaEnvelope className="me-2" /> Namba ya Simu
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Weka namba ya simu"
                      className="form-control-lg"
                      disabled={loading}
                      required
                    />
                    <Form.Text className="text-muted">
                      Tutakutumia namba ya uthibitisho (OTP) kwenye namba hii
                    </Form.Text>
                  </Form.Group>

                  <Button
                    type="submit"
                    variant="primary"
                    className="w-100 py-2 mb-3"
                    disabled={loading}
                  >
                    {loading ? <Spinner animation="border" size="sm" /> : 'Tuma Namba ya Uthibitisho'}
                  </Button>

                  <div className="text-center">
                    <Link to="/login" className="text-decoration-none">
                      <FaArrowLeft className="me-1" /> Rudi Kwenye Login
                    </Link>
                  </div>
                </Form>
              )}

              {/* Step 2: Verify Token Form */}
              {step === 2 && (
                <Form onSubmit={handleVerifyToken}>
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold">Namba ya Uthibitisho (OTP)</Form.Label>
                    <Form.Control
                      type="text"
                      name="token"
                      value={formData.token}
                      onChange={handleInputChange}
                      placeholder="Weka OTP uliyotumiwa"
                      className="form-control-lg"
                      disabled={loading}
                      required
                    />
                    <Form.Text className="text-muted">
                      Weka OTP uliyotumiwa kwenye namba au barua pepe yako
                    </Form.Text>
                  </Form.Group>

                  <Button
                    type="submit"
                    variant="primary"
                    className="w-100 py-2 mb-3"
                    disabled={loading}
                  >
                    {loading ? <Spinner animation="border" size="sm" /> : 'Thibitisha OTP'}
                  </Button>

                  <div className="text-center">
                    <Button
                      variant="link"
                      onClick={handleResendLink}
                      disabled={loading}
                      className="text-decoration-none"
                    >
                      Hujapata OTP? Tuma tena
                    </Button>
                  </div>

                  <div className="text-center mt-3">
                    <Link to="/login" className="text-decoration-none small">
                      <FaArrowLeft className="me-1" /> Rudi Kwenye Login
                    </Link>
                  </div>
                </Form>
              )}

              {/* Step 3: Reset Password Form */}
              {step === 3 && (
                <Form onSubmit={handleResetPassword}>
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold">Password Mpya</Form.Label>
                    <Form.Control
                      type="password"
                      name="new_password"
                      value={formData.new_password}
                      onChange={handleInputChange}
                      placeholder="Weka password mpya"
                      className="form-control-lg"
                      disabled={loading}
                      required
                    />
                    <Form.Text className="text-muted">
                      Password lazima iwe na herufi 8 au zaidi
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold">Thibitisha Password Mpya</Form.Label>
                    <Form.Control
                      type="password"
                      name="new_password_confirmation"
                      value={formData.new_password_confirmation}
                      onChange={handleInputChange}
                      placeholder="Rudia password mpya"
                      className="form-control-lg"
                      disabled={loading}
                      required
                    />
                  </Form.Group>

                  <Button
                    type="submit"
                    variant="primary"
                    className="w-100 py-2 mb-3"
                    disabled={loading}
                  >
                    {loading ? <Spinner animation="border" size="sm" /> : 'Badilisha Password'}
                  </Button>

                  <div className="text-center">
                    <Link to="/login" className="text-decoration-none">
                      <FaArrowLeft className="me-1" /> Rudi Kwenye Login
                    </Link>
                  </div>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ForgotPassword;